import { hash01 } from '../random/hash';
import { wallTimeOf } from '../time/zoned';
import { splitPrecipitation } from './presets';
import { historyHourStarts, type WeatherHistory, type WeatherHour } from './types';

// Approximate Onomichi normals (refine against JMA in phase 4): monthly mean daily max/min °C and wet days (≥1 mm).
const TMAX = [9.3, 10.2, 13.8, 19.3, 24.0, 27.0, 30.8, 32.4, 28.6, 23.1, 17.2, 11.7];
const TMIN = [1.0, 1.2, 4.0, 8.8, 13.8, 18.5, 23.0, 24.0, 20.1, 13.8, 7.9, 3.0];
const WET_DAYS = [5, 6, 8, 9, 9, 12, 11, 7, 9, 6, 5, 5];
const WIND_FROM = [315, 315, 300, 250, 220, 200, 190, 190, 60, 45, 315, 315];

type Regime = 'clear' | 'cloudy' | 'wet';

const DAY_MS = 86_400_000;
const WARMUP_DAYS = 14;

function monthOfDayNumber(dayNumber: number): number {
  return new Date(dayNumber * DAY_MS).getUTCMonth();
}

function wetProbability(month: number): number {
  return WET_DAYS[month]! / 30;
}

/** Two-state Markov chain (wet persists) with a fixed warm-up, so every day is a pure function of (seed, day). */
function regimeOf(seed: number, dayNumber: number): Regime {
  let wet = hash01(seed, 'wx0', dayNumber - WARMUP_DAYS) < wetProbability(monthOfDayNumber(dayNumber - WARMUP_DAYS));
  for (let d = dayNumber - WARMUP_DAYS + 1; d <= dayNumber; d++) {
    const p = wetProbability(monthOfDayNumber(d));
    const u = hash01(seed, 'wx', d);
    wet = wet ? u < Math.min(0.85, p * 2.2) : u < p * 0.7;
  }
  if (wet) return 'wet';
  return hash01(seed, 'cloudy', dayNumber) < 0.4 ? 'cloudy' : 'clear';
}

function climateHour(seed: number, time: number, timeZone: string): WeatherHour {
  const wall = wallTimeOf(time, timeZone);
  const month = wall.month - 1;
  const dayNumber = Math.floor(Date.UTC(wall.year, wall.month - 1, wall.day) / DAY_MS);
  const regime = regimeOf(seed, dayNumber);

  const jitter = (hash01(seed, 'tday', dayNumber) - 0.5) * 4;
  const tmax = TMAX[month]! + jitter - (regime === 'wet' ? 2.5 : 0);
  const tmin = TMIN[month]! + jitter + (regime === 'wet' ? 1 : 0);
  const diurnal = 0.5 + 0.5 * Math.cos((2 * Math.PI * (wall.hour - 14)) / 24);
  const temperatureC = tmin + (tmax - tmin) * diurnal;

  const raining = regime === 'wet' && hash01(seed, 'rh', time) < 0.55;
  const precipitationMm = raining ? 0.5 + hash01(seed, 'mm', time) * 2.5 : 0;
  const clouds = regime === 'wet' ? [0.95, 0.9, 0.6] : regime === 'cloudy' ? [0.5, 0.45, 0.35] : [0.05, 0.1, 0.2];
  const windSpeedMs = 1.5 + hash01(seed, 'wind', dayNumber) * 3 + (regime === 'wet' ? 3 : 0);
  const { rainMm, snowfallCm } = splitPrecipitation(precipitationMm, temperatureC);

  return {
    time,
    temperatureC,
    humidity: regime === 'wet' ? 0.92 : regime === 'cloudy' ? 0.75 : 0.62,
    visibilityM: regime === 'wet' ? 5000 : regime === 'cloudy' ? 15000 : 25000,
    precipitationMm,
    rainMm,
    snowfallCm,
    cloudLow: clouds[0]!,
    cloudMid: clouds[1]!,
    cloudHigh: clouds[2]!,
    windSpeedMs,
    windDirDeg: (WIND_FROM[month]! + (hash01(seed, 'wdir', dayNumber) - 0.5) * 60 + 360) % 360,
    gustMs: windSpeedMs * 1.7,
    code: raining ? (snowfallCm > 0 ? 71 : 61) : regime === 'wet' ? 3 : regime === 'cloudy' ? 2 : 0,
    source: 'climate',
  };
}

export function climateHistory(instant: number, seed: number, timeZone: string): WeatherHistory {
  return { hours: historyHourStarts(instant).map((time) => climateHour(seed, time, timeZone)), daily: [] };
}
